import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Skeleton,
  Typography,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffectAsync } from '../../reactHelper';
import { useTranslation } from '../../common/components/LocalizationProvider';
import PageLayout from '../../common/components/PageLayout';
import useSettingsStyles from '../common/useSettingsStyles';
import fetchOrThrow from '../../common/util/fetchOrThrow';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { toast } from '../../common/components/premium';

const EditItemView = ({
  children,
  endpoint,
  item,
  setItem,
  defaultItem,
  validate,
  onItemSaved,
  menu,
  breadcrumbs,
}) => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const tokens = usePremiumTokens();
  const t = useTranslation();

  const [saving, setSaving] = useState(false);

  const { id } = useParams();

  const revenda = useSelector((state) => state.session.revenda);

  useEffectAsync(async () => {
    if (!item) {
      if (id) {
        const response = await fetchOrThrow(`/api/${endpoint}/${id}`);
        setItem(await response.json());
      } else {
        setItem(defaultItem || {});
      }
    }
  }, [id, item, defaultItem]);

  const handleSave = async () => {
    setSaving(true);
    const toastId = toast.loading(t('sharedSaving'));

    try {
      let url = `/api/${endpoint}`;
      if (id) {
        url += `/${id}`;
      }

      const response = await fetchOrThrow(url, {
        method: !id ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });

      const savedItem = await response.json();

      if (onItemSaved) {
        onItemSaved(savedItem);
      }

      toast.success(t('sharedSaved'), { id: toastId });
      navigate(-1);
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || t('errorGeneral'), { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout menu={menu} breadcrumbs={breadcrumbs}>
      <Container maxWidth={false} className={classes.container}>
        {item ? (
          children
        ) : (
          <Accordion
            defaultExpanded
            className={classes.accordionPremium}
            sx={{
              backgroundColor: tokens?.surface?.glass,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: `${tokens?.radius?.lg || '12px'} !important`,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: tokens?.accent?.primary }} />}
              className={classes.accordionSummaryPremium}
            >
              <Typography variant="subtitle1">
                <Skeleton
                  width="10em"
                  sx={{
                    bgcolor: tokens?.isDark
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                  }}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetailsPremium}>
              {[...Array(3)].map((_, i) => (
                <Skeleton
                  key={-i}
                  width="100%"
                  sx={{
                    mb: 2,
                    borderRadius: tokens?.radius?.md,
                    bgcolor: tokens?.isDark
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.08)',
                  }}
                >
                  <TextField />
                </Skeleton>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Premium Action Buttons */}
        <Box
          className={classes.buttons}
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            gap: 2,
            mt: 3,
            pt: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={!item}
            className={classes.buttonSecondary}
            sx={{
              flex: 1,
              maxWidth: 200,
              py: 1.25,
              borderRadius: tokens?.radius?.md,
              borderColor: tokens?.border?.default,
              color: tokens?.text?.primary,
              fontWeight: tokens?.typography?.fontWeight?.medium,
              textTransform: 'none',
              transition: tokens?.transitions?.all,
              '&:hover': {
                borderColor: tokens?.accent?.primary,
                backgroundColor: tokens?.accent?.subtle,
                color: tokens?.accent?.primary,
              },
              '&:disabled': {
                borderColor: tokens?.border?.subtle,
                color: tokens?.text?.muted,
              },
            }}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!item || !validate() || saving}
            className={classes.buttonPrimary}
            sx={{
              flex: 1,
              maxWidth: 200,
              py: 1.25,
              borderRadius: tokens?.radius?.md,
              backgroundColor: tokens?.accent?.primary,
              color: tokens?.text?.onAccent,
              fontWeight: tokens?.typography?.fontWeight?.medium,
              textTransform: 'none',
              boxShadow: tokens?.shadows?.sm,
              transition: tokens?.transitions?.all,
              '&:hover': {
                backgroundColor: tokens?.accent?.hover,
                boxShadow: tokens?.shadows?.glowAccent,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&:disabled': {
                backgroundColor: tokens?.border?.default,
                color: tokens?.text?.muted,
                boxShadow: 'none',
              },
            }}
          >
            {saving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                {t('sharedSaving')}
              </>
            ) : (
              t('sharedSave')
            )}
          </Button>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default EditItemView;
